import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://floridahousesforsale.deals';

const requestSchema = {
  '@context': 'https://schema.org',
  '@type': 'ContactPage',
  name: 'Request More Information – Florida Real Estate',
  description: 'Request information about Florida homes for sale. Tell Amparo Jarquin what you are looking for and receive a response within 24 hours.',
  url: `${SITE_URL}/request`,
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Request Information', item: `${SITE_URL}/request` },
    ],
  },
};

export default function RequestPage() {
  const [submitted, setSubmitted] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d7e0b31noju8nk5a1hpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      <SEO
        title="Request Florida Real Estate Information | Amparo Jarquin"
        description="Request information about Florida homes for sale. Fill out the form and Amparo Jarquin will respond within 24 hours to help with your Florida real estate needs."
        keywords="Florida real estate inquiry, request home information Florida, contact Florida realtor, Florida property information"
        canonical="/request"
        schema={requestSchema}
      />
      <Navbar />
      <section className="relative pt-36 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/1913ace79e1b65b6a3bcf6ddb470675a.jpg"
            alt="Florida home"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-stone-900/62"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <p className="text-amber-400 text-xs font-semibold tracking-widest uppercase mb-3">We&apos;re Here to Help</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Request More Information</h1>
          <p className="text-white/70 max-w-xl mx-auto text-base">Tell us what you&apos;re looking for and Amparo will be in touch within 24 hours.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 py-20">
        {submitted ? (
          <div className="bg-white rounded-lg border border-amber-100 p-16 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-6">
              <i className="ri-checkbox-circle-fill text-amber-500 text-4xl"></i>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h2>
            <p className="text-gray-500 text-sm leading-relaxed max-w-md mx-auto">Your request has been received. Amparo Jarquin will contact you within 24 hours. We look forward to helping you find your perfect Florida home!</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-100 p-8 md:p-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Contact Amparo Jarquin</h2>
            <p className="text-gray-500 text-sm mb-8">Florida Homes for Sale &mdash; Licensed Real Estate Agent</p>

            <form data-readdy-form id="request-more-info" onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">First Name *</label>
                  <input name="first_name" type="text" required placeholder="Jane" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Last Name *</label>
                  <input name="last_name" type="text" required placeholder="Doe" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Email Address *</label>
                <input name="email" type="email" required placeholder="jane@example.com" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Phone Number</label>
                <input name="phone" type="tel" placeholder="(305) 000-0000" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">I Am Interested In *</label>
                <select name="interest" required className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer">
                  <option value="">Select an option...</option>
                  <option value="Buying a Home">Buying a Home</option>
                  <option value="Selling a Home">Selling a Home</option>
                  <option value="Both Buying and Selling">Both Buying and Selling</option>
                  <option value="Investment Property">Investment Property</option>
                  <option value="General Information">General Information</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Price Range</label>
                <select name="price_range" className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 focus:outline-none focus:border-amber-400 transition-colors bg-white cursor-pointer">
                  <option value="">Select a range...</option>
                  <option value="Under $300K">Under $300,000</option>
                  <option value="$300K - $600K">$300,000 – $600,000</option>
                  <option value="$600K - $1M">$600,000 – $1,000,000</option>
                  <option value="$1M - $2M">$1,000,000 – $2,000,000</option>
                  <option value="Over $2M">Over $2,000,000</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Preferred Florida Area</label>
                <input name="preferred_area" type="text" placeholder="e.g. Miami, Orlando, Tampa, Naples..." className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2">Message</label>
                <textarea
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder="Tell us more about what you're looking for..."
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="w-full border border-gray-200 rounded-md px-4 py-3 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-amber-400 transition-colors resize-none"
                ></textarea>
                <p className="text-xs text-gray-400 text-right mt-1">{charCount}/500</p>
              </div>
              <button
                type="submit"
                className="w-full py-4 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer text-sm tracking-wide"
              >
                Send Request to Amparo
              </button>
              <p className="text-xs text-gray-400 text-center">By submitting this form, you agree to be contacted by Florida Homes for Sale regarding your inquiry.</p>
            </form>
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}
